import React from "react";
import { fetchQuery, graphql } from "react-relay";
import {
	components,
	type CSSObjectWithLabel,
	type DropdownIndicatorProps,
	type OptionProps,
	type SingleValueProps,
	type Theme,
} from "react-select";
import AsyncSelect from "react-select/async";

import { faCalendar, faCalendarDay, faChevronRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faChevronDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { throttle } from "lodash";
import moment from "moment";

import { Twix } from "~/lib/twix";
import { environment } from "~/models";
import type { Omit, OptionalExceptFor } from "~/types";
import AsyncSelector from "~/ui/selectors/AsyncSelector";

import type { MeetingSelect_event$data as DirtyMeetingEvent } from "~/__generated__/MeetingSelect_event.graphql";
import type { MeetingSelectQuery as QueryType } from "~/__generated__/MeetingSelectQuery.graphql";

import styles from "~/common/css/MeetingSelect.module.css";

export type MeetingEvent = OptionalExceptFor<
	Omit<DirtyMeetingEvent, " $fragmentType">,
	"guid" | "start" | "end" | "title"
>;

export type MeetingEventOption = MeetingEvent & {
	value: string;
	label: string;
};

const IndicatorSeparator = () => <React.Fragment />;

// Hack to work around this not being exported anywhere
const DropdownIndicator: NonNullable<typeof components.DropdownIndicator> = components.DropdownIndicator as NonNullable<
	typeof components.DropdownIndicator
>;

const CustomDropdownIndicator = (props: DropdownIndicatorProps<MeetingEvent, false>) => {
	return (
		<DropdownIndicator {...props}>
			<FontAwesomeIcon icon={faChevronDown} className={styles.dropdownIndicator} />
		</DropdownIndicator>
	);
};

const CustomSingleValue = (props: SingleValueProps<MeetingEvent, false>) => {
	const { children, ...rest } = props;
	return (
		<components.SingleValue {...rest}>
			<FontAwesomeIcon icon={faCalendarDay} className={styles.calendarIcon} />
			{children}
		</components.SingleValue>
	);
};

const CustomOption = (props: OptionProps<MeetingEvent, false>) => {
	return (
		<div title={props.data.title}>
			<components.Option {...props} />
		</div>
	);
};

graphql`
	fragment MeetingSelect_event on Event {
		id
		guid
		title
		start
		end
		type
		hasNote
		attendees {
			email
			image
			fullName
			user {
				...UserSelect_user @relay(mask: false)
			}
		}
	}
`;

const MeetingSelectQuery = graphql`
	query MeetingSelectQuery(
		$start: DateTime
		$end: DateTime
		$query: String
		$recurring: Boolean
		$oneOnOne: ID
		$guid: String
		$nextOccurrencesCount: Int
		$oneOnOneOnly: Boolean
	) {
		events(
			start: $start
			end: $end
			query: $query
			recurring: $recurring
			oneOnOne: $oneOnOne
			guid: $guid
			nextOccurrencesCount: $nextOccurrencesCount
			oneOnOneOnly: $oneOnOneOnly
		) {
			edges {
				node {
					...MeetingSelect_event @relay(mask: false)
				}
			}
		}
	}
`;

const feedbackSelectStyles = {
	container: (base: object) => ({ ...base, flexGrow: 1, zIndex: 3, padding: 0, height: "55px" }),
	control: (base: object) => ({
		...base,
		borderWidth: 1,
		padding: "0.3em 0.3em 0.3em 1em",
		backgroundColor: "transparent",
		height: "55px",
		cursor: "pointer",
		boxShadow: "none",
		borderColor: "#ADBCC3",
	}),
	singleValue: (base: object): CSSObjectWithLabel => ({
		...base,
		position: "relative",
		transform: "unset",
		top: "unset",
	}),
	valueContainer: (base: object) => ({ ...base, padding: "2px 5px" }),
	option: (base: CSSObjectWithLabel, { isFocused, isSelected }: { isFocused: boolean; isSelected: boolean }) => {
		let backgroundColor = base.backgroundColor;
		if (isSelected) {
			backgroundColor = "rgba(0, 0, 0, 0.03)";
		} else if (isFocused) {
			backgroundColor = "rgba(0, 0, 0, 0.05)";
		}

		return {
			...base,
			backgroundColor,
			color: "rgba(0, 0, 0, 0.95)",
			cursor: "pointer",
			borderBottom: "1px solid #FAFAFA",
		};
	},
};

interface MeetingSelectProps {
	value: MeetingEvent | undefined | null;
	onChange: (value: MeetingEvent | undefined | null) => void;
	upcomingOption: boolean;
	onlyNoteless: boolean;
	recurringOnly: boolean;
	useFeedbackStyle: boolean;
	reverse: boolean;
	oneOnOne: string;
	oneOnOneOnly: boolean;
	start: Date;
	end: Date;
	className?: string;
	error?: boolean;
	menuIsOpen?: boolean;
	optionChevron?: boolean;
	placeholder?: React.ReactNode;
	optionIcon?: boolean;
	isClearable?: boolean;
	eventGuid?: string;
	nextOccurrencesCount?: number;
	menuPlacement?: React.ComponentProps<typeof AsyncSelect>["menuPlacement"];
	menuPortalTarget?: React.ComponentProps<typeof AsyncSelect>["menuPortalTarget"];
}

export const UNSCHEDULED_MEETING = {
	id: "UPCOMING",
	guid: "UPCOMING",
	title: "Unscheduled",
	start: "",
	end: "",
	type: "SINGLE",
	hasNote: false,
	attendees: [],
} as const;

interface MeetingSelectState {
	value: MeetingEvent | undefined | null;
}

export default class MeetingSelect extends React.Component<MeetingSelectProps, MeetingSelectState> {
	static defaultProps = {
		isMulti: false,
		upcomingOption: false,
		onlyNoteless: false,
		recurringOnly: false,
		useModalStyle: false,
		reverse: false,
		oneOnOne: null,
		oneOnOneOnly: false,
		start: null,
		end: null,
		placeholder: "Select...",
		optionIcon: false,
		isClearable: false,
	};

	constructor(props: MeetingSelectProps) {
		super(props);

		this.state = { value: props.value ? this.convertToSelectorOption(props.value) : undefined };
	}

	componentWillReceiveProps(nextProps: MeetingSelectProps) {
		this.setState({ value: nextProps.value });
	}

	formatEvent = (event: MeetingEvent) => {
		if (event.type == "RECURRING") {
			return event.title;
		} else {
			let time = "Not yet scheduled";
			if (event.start && event.end) {
				time = new Twix(moment(event.start), moment(event.end)).format({
					implicitYear: true,
					implicitMinutes: true,
				});
			}

			if (!this.props.useFeedbackStyle) {
				return (
					<>
						<span style={{ alignSelf: "baseline" }}>{event.title}</span>
						<small style={{ opacity: 0.4, marginLeft: "10px", alignSelf: "baseline" }}>{time}</small>
					</>
				) as unknown as string; // Work around AsyncSelect label type
			} else {
				return (
					<div className={styles.option}>
						{this.props.optionIcon && (
							<div className="meeting-selector--feedback__selected-icon">
								<FontAwesomeIcon icon={faCalendar} />
							</div>
						)}
						<div className={styles.displayValue}>
							<div className={cx(styles.selectedTitle, "meeting-selector--feedback__selected-title")}>
								{event.title}
							</div>
							<div className={cx(styles.selectedTime, "meeting-selector--feedback__selected-time")}>{time}</div>
						</div>
						{this.props.optionChevron && <FontAwesomeIcon className={styles.selectedChevron} icon={faChevronRight} />}
					</div>
				) as unknown as string; // Work around AsyncSelect label type
			}
		}
	};

	getEventGuid = (event: MeetingEvent): string => event.guid;

	fetch = (query: string, resolve: (events: MeetingEventOption[]) => void) => {
		const {
			start,
			end,
			recurringOnly,
			oneOnOne,
			oneOnOneOnly,
			reverse,
			onlyNoteless,
			upcomingOption,
			eventGuid,
			nextOccurrencesCount,
		} = this.props;

		const vars = {
			start: start ? start.toISOString() : moment().subtract(1, "month").format(),
			end: end ? end.toISOString() : moment().format(),
			query: query,
			recurring: recurringOnly,
			oneOnOne: oneOnOne,
			guid: eventGuid,
			nextOccurrencesCount,
			oneOnOneOnly: oneOnOneOnly,
		};

		fetchQuery<QueryType>(environment, MeetingSelectQuery, vars)
			.toPromise()
			.then(result => {
				if (!result) return;
				const { events } = result;
				const edges = reverse ? events.edges.slice().reverse() : events.edges;
				let nodes = edges.map((edge: { node: MeetingEvent | null } | null) => edge && edge.node) as MeetingEvent[];

				if (onlyNoteless) {
					nodes = nodes.filter(event => !event.hasNote);
				}

				if (upcomingOption) {
					nodes = [UNSCHEDULED_MEETING, ...nodes];
				}

				const options = nodes.map(this.convertToSelectorOption);

				resolve(options);
			});
	};

	throttledFetch: (query: string, resolve: (events: MeetingEventOption[]) => void) => void = throttle(this.fetch, 250);

	fetchOptions = (query: string) => new Promise<MeetingEventOption[]>(resolve => this.throttledFetch(query, resolve));

	getTheme = (theme: Theme) => {
		return {
			...theme,
			colors: {
				...theme.colors,
				primary: "hsl(0,0%,80%)",
				primary50: "rgba(0, 0, 0, 0.05)",
			},
		};
	};

	convertToSelectorOption = (event: MeetingEvent): MeetingEventOption => {
		return {
			...event,
			value: event.guid,
			label: this.formatEvent(event),
		};
	};

	convertFromSelectorOption = (option: MeetingEventOption): MeetingEvent => {
		const { value: _value, label: _label, ...rest } = option;
		return rest;
	};

	render() {
		const { useFeedbackStyle, isClearable, onChange, className, error, placeholder, menuPlacement, menuPortalTarget } =
			this.props;
		const customComponents = { CustomOption, IndicatorSeparator, CustomDropdownIndicator, CustomSingleValue };

		return (
			<AsyncSelector<MeetingEventOption>
				menuPortalTarget={useFeedbackStyle ? null : menuPortalTarget}
				menuPlacement={menuPlacement}
				cacheOptions
				defaultOptions
				isClearable={isClearable}
				loadOptions={this.fetchOptions}
				value={this.state.value ? this.convertToSelectorOption(this.state.value) : undefined}
				onChange={onChange}
				getOptionLabel={this.formatEvent}
				getOptionValue={this.getEventGuid}
				isSearchable
				className={cx(className, { error, ["meeting-selector--feedback"]: useFeedbackStyle })}
				classNamePrefix={useFeedbackStyle ? "meeting-selector--feedback" : undefined}
				styles={useFeedbackStyle ? feedbackSelectStyles : undefined}
				components={useFeedbackStyle ? customComponents : {}}
				theme={useFeedbackStyle ? this.getTheme : undefined}
				menuIsOpen={this.props.menuIsOpen}
				placeholder={placeholder}
			/>
		);
	}
}
